import React from 'react';
import { PageBanner } from '../components/PageBanner';
import { ContactCTA } from '../components/ContactCTA';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { ShieldCheckIcon, HeartIcon, ActivityIcon, DropletIcon, CheckCircleIcon } from 'lucide-react';
export function FoleyCarePage() {
  const {
    ref: introRef,
    isVisible: isIntroVisible
  } = useScrollAnimation();
  const {
    ref: benefitsRef,
    isVisible: isBenefitsVisible
  } = useScrollAnimation();
  const {
    ref: detailsRef,
    isVisible: isDetailsVisible
  } = useScrollAnimation();
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useScrollAnimation();
  return <main data-id="element-751">
      <PageBanner title="Foley Catheter Management" subtitle="Professional urinary catheter care focused on comfort and infection prevention." breadcrumbs={[{
      label: 'Services',
      path: '/services'
    }, {
      label: 'Foley Care',
      path: '/services/foley-care'
    }]} data-id="element-752" />

      {/* Intro Section */}
      <section className="py-16 md:py-20 bg-light-alt" data-id="element-753">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-754">
          <div ref={introRef} className={`transition-all duration-1000 ease-out ${isIntroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-755">
            <div className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-6" data-id="element-756">
              <DropletIcon className="h-4 w-4 text-primary" data-id="element-757" />
              <span className="text-primary text-sm font-semibold" data-id="element-758">
                Urological Care
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6 leading-tight" data-id="element-759">
              Safe, Comfortable Catheter Management
            </h2>
            <div className="space-y-5 text-dark-muted text-lg leading-relaxed" data-id="element-760">
              <p data-id="element-761">
                Professional urinary catheter care focused on comfort and
                infection prevention. We monitor output, perform routine
                changes, and implement strict hygiene protocols to reduce the
                risk of UTIs.
              </p>
            </div>

            {/* RN Badge */}
            <div className="mt-8 flex items-center bg-primary/5 border border-primary/10 rounded-xl p-5" data-id="element-762">
              <ShieldCheckIcon className="h-10 w-10 text-primary mr-4 flex-shrink-0" data-id="element-763" />
              <div data-id="element-764">
                <h4 className="font-bold text-dark" data-id="element-765">RN Supervised Care</h4>
                <p className="text-sm text-dark-muted" data-id="element-766">
                  All catheter insertions and care plans are managed by skilled
                  Registered Nurses.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-20 bg-light" data-id="element-767">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-768">
          <div ref={benefitsRef} className={`transition-all duration-1000 ease-out ${isBenefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-769">
            <div className="text-center mb-14" data-id="element-770">
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4" data-id="element-771">
                Comprehensive Catheter Support
              </h2>
              <div className="w-16 h-1.5 bg-secondary mx-auto mb-6 rounded-full" data-id="element-772"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-773">
              {[{
              icon: ShieldCheckIcon,
              title: 'Infection Prevention',
              desc: 'Strict adherence to sterile techniques to minimize the risk of urinary tract infections.'
            }, {
              icon: HeartIcon,
              title: 'Comfort Focus',
              desc: 'Ensuring proper securement and positioning to maximize patient comfort.'
            }, {
              icon: ActivityIcon,
              title: 'Routine Monitoring',
              desc: 'Careful observation of output and early detection of potential complications.'
            }, {
              icon: DropletIcon,
              title: 'Hygiene Protocols',
              desc: 'Meticulous daily care of the catheter site and drainage system.'
            }].map((benefit, idx) => <div key={idx} className="bg-light-alt rounded-2xl p-7 shadow-card hover:shadow-card-hover border border-gray-200 transition-all duration-300 transform hover:-translate-y-1 group" data-id="element-774">
                  <div className="bg-primary/10 w-14 h-14 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary transition-colors duration-300" data-id="element-775">
                    <benefit.icon className="h-7 w-7 text-primary group-hover:text-white transition-colors duration-300" data-id="element-776" />
                  </div>
                  <h3 className="text-lg font-bold text-dark mb-3" data-id="element-777">
                    {benefit.title}
                  </h3>
                  <p className="text-dark-muted text-sm leading-relaxed" data-id="element-778">
                    {benefit.desc}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Details Section */}
      <section className="py-16 md:py-20 bg-light-alt" data-id="element-779">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-780">
          <div ref={detailsRef} className={`transition-all duration-1000 ease-out ${isDetailsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-781">
            <h2 className="text-3xl font-bold text-dark mb-6" data-id="element-782">
              Our Catheter Care Services Include
            </h2>
            <p className="text-dark-muted text-lg leading-relaxed mb-8" data-id="element-783">
              We provide expert management of indwelling catheters to ensure
              safety, function, and comfort for our patients at home.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-784">
              {['Catheter insertion & changes', 'Urinary output monitoring', 'UTI prevention protocols', 'Strict hygiene maintenance', 'Patient comfort management', 'Catheter site care'].map((item, idx) => <div key={idx} className="flex items-center p-4 bg-light rounded-xl border border-gray-200" data-id="element-785">
                  <CheckCircleIcon className="h-6 w-6 text-secondary mr-3 flex-shrink-0" data-id="element-786" />
                  <span className="text-dark font-medium" data-id="element-787">{item}</span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-light" data-id="element-788">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-789">
          <div ref={ctaRef} className={`bg-primary rounded-3xl p-10 md:p-14 text-center shadow-xl relative overflow-hidden transition-all duration-1000 ease-out ${isCtaVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'}`} data-id="element-790">
            <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(#ffffff 2px, transparent 2px)',
            backgroundSize: '30px 30px'
          }} data-id="element-791"></div>
            <div className="relative z-10" data-id="element-792">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{
              fontFamily: "'Cinzel', serif"
            }} data-id="element-793">
                Need Expert Catheter Care?
              </h2>
              <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-794">
                Contact us today to discuss your care needs and schedule a
                consultation with our nursing team.
              </p>
              <a href="#/contact" className="inline-block bg-white text-primary-dark hover:bg-light-alt px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1" data-id="element-795">
                Contact Us Today
              </a>
            </div>
          </div>
        </div>
      </section>

      <ContactCTA data-id="element-796" />
    </main>;
}